import { useState, useEffect, useCallback } from "react";
import { resolveRouterPath } from "files/hooks/useFiles";
import FileInfo from "files/types/interfaces/fileInfo";
import { Info } from "lucide-react";
import { Accordion } from "~/components/ui/accordion";
import { StatsCard } from "~/components/ui/stats-card";
import { useStepperContext } from "~/components/ui/stepper";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "~/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { Tooltip, TooltipTrigger, TooltipContent } from "~/components/ui/tooltip";
import CloudFilePicker from "./CloudFilePicker";
import { FileUploadArea } from "./FileUploadArea";
import {
  SourceFileLabel,
  ValidationJobCard,
  ValidationJobCardLoading,
  ValidationErrorDownloads,
  StatusBadge,
} from "./SharedComponents";
import { useIngestionContext } from "./context/IngestionContext";
import { FileValidationStepperState, ValidationJob, ColumnProfileDisplay } from "./types";

/** Convert file key or S3 URI to frontend-friendly display path */
const formatFileForDisplay = (fileKey: string | null | undefined): string | null => {
  if (!fileKey) {
    return null;
  }

  // Strip s3://bucket-name/ prefix if present
  let bareKey = fileKey;
  if (fileKey.startsWith("s3://")) {
    const withoutProtocol = fileKey.slice(5);
    const slashIndex = withoutProtocol.indexOf("/");
    if (slashIndex !== -1) {
      bareKey = withoutProtocol.slice(slashIndex + 1);
    }
  }

  return resolveRouterPath(bareKey);
};

/** Format a null percentage for display, rounding to 1 decimal place */
const formatNullPercent = (value: number): string => {
  if (value === 0) {
    return "0";
  }
  if (value < 0.1) {
    return "< 0.1";
  }
  if (value === 100) {
    return "100";
  }
  return value.toFixed(1);
};

/** Renders a formatted null % value */
const NullPercentCell = ({ value }: { value: number }) => {
  return <span>{formatNullPercent(value)}%</span>;
};

/** Expected validation job titles in display order */
const EXPECTED_VALIDATION_JOBS = ["File Format Detection", "Header Detection", "File Structure"];

/** Step 1: File selection via upload or cloud storage */
export const FileUploadStep = () => {
  const {
    selectedFile,
    selectFile,
    clearFile,
    pendingLocalFile,
    setPendingLocalFile,
    isUploading,
    uploadProgress,
    error,
    hasIngestion,
  } = useIngestionContext();
  const { stepperState } = useStepperContext<FileValidationStepperState>();

  const { ingestion, recoveredSourceFile, isLoadingIngestion } = stepperState;

  // Existing source file from ingestion record or job results (for resume display)
  const existingSourceKey = ingestion?.source_file || recoveredSourceFile;
  const isLoadingFileInfo = hasIngestion && isLoadingIngestion && !existingSourceKey;

  const selectedFileMatchesIngestion =
    !!selectedFile &&
    (selectedFile.s3_uri === recoveredSourceFile ||
      selectedFile.key === recoveredSourceFile ||
      (!!ingestion?.source_file &&
        (selectedFile.s3_uri === ingestion.source_file ||
          selectedFile.key === ingestion.source_file)));
  const isStartingFresh =
    hasIngestion && (!!pendingLocalFile || (!!selectedFile && !selectedFileMatchesIngestion));

  const handlePendingFileSelected = (file: File | null) => {
    setPendingLocalFile(file);
    if (file && selectedFile) {
      clearFile();
    }
  };

  // Guard against the recovered file so async hydration doesn't flip the button to "Validate".
  const existingKeyNormalized = existingSourceKey?.startsWith("s3://")
    ? existingSourceKey.slice(existingSourceKey.indexOf("/", 5) + 1)
    : existingSourceKey;

  const handleCloudFileSelected = useCallback(
    (file: FileInfo) => {
      if (existingKeyNormalized && file.key === existingKeyNormalized) {
        return;
      }
      selectFile(file);
    },
    [selectFile, existingKeyNormalized]
  );

  const existingFileName =
    hasIngestion && existingSourceKey ? formatFileForDisplay(existingSourceKey) : null;

  const newFileName = pendingLocalFile
    ? pendingLocalFile.name
    : selectedFile
      ? formatFileForDisplay(selectedFile.key)
      : null;

  const sourceFileLabelProps = isStartingFresh
    ? { label: "New file:", fileName: newFileName }
    : { fileName: existingFileName };

  if (isLoadingFileInfo) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse space-y-4">
          <div className="h-5 w-64 bg-gray-200 rounded" />
          <div className="h-32 bg-gray-100 rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      <SourceFileLabel {...sourceFileLabelProps} />

      <Tabs
        defaultValue={hasIngestion && !pendingLocalFile ? "cloud-storage" : "upload"}
        className="gap-4"
      >
        <TabsList className="w-fit">
          <TabsTrigger value="upload">Upload</TabsTrigger>
          <TabsTrigger value="cloud-storage">Cloud Storage</TabsTrigger>
        </TabsList>
        <TabsContent value="upload">
          <FileUploadArea
            onFileSelected={handlePendingFileSelected}
            selectedFile={pendingLocalFile}
            onClearFile={() => setPendingLocalFile(null)}
            isUploading={isUploading}
            uploadProgress={uploadProgress}
          />
        </TabsContent>
        <TabsContent value="cloud-storage">
          <CloudFilePicker
            selectedFile={pendingLocalFile ? null : selectedFile}
            onSelectFile={handleCloudFileSelected}
            placeholder={
              pendingLocalFile ? "Using uploaded file instead..." : "Select source file..."
            }
            isLoading={isLoadingFileInfo}
            disabled={!!pendingLocalFile}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

/** Step 2: Display validation job results and status */
export const FileValidationStep = () => {
  const { selectedFile, pendingLocalFile } = useIngestionContext();
  const { stepperState } = useStepperContext<FileValidationStepperState>();

  const {
    validationState,
    isLoadingIngestion,
    ingestion,
    recoveredSourceFile,
    isProfiling,
    profilingError,
    dataProfile,
  } = stepperState;
  const isProfilingComplete = !isProfiling && !!dataProfile;

  // File display priority: current selection > pending upload name > ingestion record > job results
  const sourceFileKey = selectedFile?.key || ingestion?.source_file || recoveredSourceFile;
  const pendingFileName = pendingLocalFile?.name;
  const displayFileName = sourceFileKey
    ? formatFileForDisplay(sourceFileKey)
    : pendingFileName || null;

  const getOverallStatus = (): "passed" | "warning" | "running" | "failed" => {
    if (!validationState?.isComplete) {
      return "running";
    }
    if (validationState.hasErrors) {
      return "failed";
    }
    if (validationState.hasWarnings) {
      return "warning";
    }
    if (!validationState.hasErrors && !dataProfile && !profilingError) {
      return "running";
    }
    return "passed";
  };

  const [openItems, setOpenItems] = useState<string[]>([]);

  useEffect(() => {
    if (!validationState?.jobs) {
      return;
    }
    const failedOrWarning = validationState.jobs
      .filter((job: ValidationJob) => job.status === "warning" || job.status === "failed")
      .map((job: ValidationJob) => job.title);
    if (failedOrWarning.length > 0) {
      setOpenItems((prev) => {
        const merged = new Set([...prev, ...failedOrWarning]);
        return Array.from(merged);
      });
    }
  }, [validationState?.jobs]);

  if (isLoadingIngestion) {
    return (
      <div className="space-y-6">
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-700">Loading ingestion data...</p>
        </div>
      </div>
    );
  }

  // Show named loading skeletons while waiting for first poll results
  if (!validationState) {
    return (
      <div className="space-y-6">
        <div>
          <SourceFileLabel fileName={displayFileName} />
          <div className="flex items-center gap-2 mt-2">
            <span className="text-sm text-gray-600">Status:</span>
            <StatusBadge status="running" />
          </div>
        </div>
        <div className="border rounded-lg overflow-hidden">
          <Accordion type="multiple" className="divide-y">
            {EXPECTED_VALIDATION_JOBS.map((title) => (
              <ValidationJobCardLoading key={title} title={title} />
            ))}
          </Accordion>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 flex-1 min-h-0">
      <div className="shrink-0">
        <SourceFileLabel fileName={displayFileName} />
        {/* Top-level status badge */}
        <div className="flex items-center gap-2 mt-2">
          <span className="text-sm text-gray-600">Status:</span>
          <StatusBadge status={getOverallStatus()} />
        </div>
      </div>

      {/* Error/warning/profiling-error alert — above tasks, below status */}
      {validationState.isComplete &&
        (validationState.hasErrors || profilingError || validationState.hasWarnings) && (
          <div
            className={
              validationState.hasErrors || profilingError
                ? "shrink-0 rounded-lg border border-red-200 bg-red-50 p-3"
                : "shrink-0 rounded-lg border border-yellow-200 bg-yellow-50 p-3"
            }
          >
            {validationState.hasErrors ? (
              <>
                <p className="text-sm text-red-700 font-medium">Validation failed</p>
                <p className="text-sm text-red-600">
                  Please review the errors below and correct your file before proceeding.
                </p>
                {validationState.summary.errorRows > 0 && (
                  <ValidationErrorDownloads
                    invalidRowsFilePath={validationState.invalidRowsFilePath}
                    rowDetailsFilePath={validationState.rowDetailsFilePath}
                  />
                )}
              </>
            ) : profilingError ? (
              <>
                <p className="text-sm text-red-700 font-medium">Data profiling failed</p>
                <p className="text-sm text-red-600">
                  File validation passed, but data profiling encountered an error. Please try again
                  with a different file.
                </p>
              </>
            ) : (
              <>
                <p className="text-sm text-yellow-800 font-medium">
                  Validation completed with warnings
                </p>
                <p className="text-sm text-yellow-700">
                  Review the warnings below. You may proceed, but some data may require attention.
                </p>
                {validationState.summary.errorRows > 0 && (
                  <ValidationErrorDownloads
                    invalidRowsFilePath={validationState.invalidRowsFilePath}
                    rowDetailsFilePath={validationState.rowDetailsFilePath}
                  />
                )}
              </>
            )}
          </div>
        )}

      {/* Validation Jobs + Data Profiling */}
      <div className="flex-1 min-h-0 border rounded-lg overflow-y-auto">
        <Accordion
          type="multiple"
          value={openItems}
          onValueChange={setOpenItems}
          className="divide-y"
        >
          {EXPECTED_VALIDATION_JOBS.map((expectedTitle) => {
            const matchedJob = validationState.jobs.find(
              (job: ValidationJob) => job.title === expectedTitle
            );
            if (matchedJob) {
              return <ValidationJobCard key={expectedTitle} {...matchedJob} />;
            }
            if (!validationState.isComplete) {
              return <ValidationJobCardLoading key={expectedTitle} title={expectedTitle} />;
            }
            return null;
          })}
          {validationState.jobs
            .filter((job: ValidationJob) => !EXPECTED_VALIDATION_JOBS.includes(job.title))
            .map((job: ValidationJob, idx: number) => (
              <ValidationJobCard key={`extra-${idx}`} {...job} />
            ))}

          {validationState.isComplete && !validationState.hasErrors && (
            <ValidationJobCard
              title="Data Profiling"
              description={`Analyzing column types and data quality across ${validationState.summary.cleanRows} clean rows`}
              result={
                profilingError
                  ? "Profiling failed"
                  : isProfilingComplete
                    ? `Profiling complete — ${validationState.summary.cleanRows} clean rows analyzed`
                    : "Profiling in progress"
              }
              status={profilingError ? "failed" : isProfilingComplete ? "passed" : "running"}
              messages={profilingError ? [profilingError.message] : undefined}
            />
          )}
        </Accordion>
      </div>

      {/* Review Validation Summary */}
      {validationState.isComplete && (
        <div className="shrink-0">
          <h4 className="font-medium mb-3">Review Validation Summary</h4>
          <div className="grid grid-cols-2 gap-y-1 text-xs">
            {[
              ["Detected File Format", validationState.fileType?.toUpperCase() || "—"],
              ["Total Rows in File", validationState.summary.totalRows],
              ["Header Row", validationState.summary.headerRow ? "yes" : "no"],
              ["Data Rows", validationState.summary.dataRows],
              ["Data Rows with Errors", validationState.summary.errorRows],
              ["Clean Data Rows", validationState.summary.cleanRows],
            ].map(([label, value]) => (
              <div key={String(label)} className="flex gap-2">
                <span className="text-gray-600">{label}</span>
                <span className="font-medium">{String(value)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * SummaryStep
 *
 * Uses IngestionContext for selected file info.
 * Uses StepperContext for data profile.
 */
export const SummaryStep = () => {
  const { selectedFile } = useIngestionContext();
  const { stepperState } = useStepperContext<FileValidationStepperState>();

  const { dataProfile, profilingError, isProfiling, ingestion, recoveredSourceFile } = stepperState;

  // Determine the source file to display:
  // 1. selectedFile from context (user just selected it in this session)
  // 2. ingestion.source_file from backend (resumed ingestion)
  // 3. recoveredSourceFile extracted from job results
  const sourceFileKey = selectedFile?.key || ingestion?.source_file || recoveredSourceFile;
  const displayFileName = sourceFileKey ? formatFileForDisplay(sourceFileKey) : null;

  // Show loading state
  if (isProfiling) {
    return (
      <div className="space-y-6">
        <div className="p-8 text-center">
          <p className="text-gray-600">Analyzing data profile...</p>
        </div>
      </div>
    );
  }

  // Show error state if profiling failed
  if (profilingError) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0">
              <svg
                className="h-5 w-5 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800">Profiling Failed</h3>
              <p className="mt-1 text-sm text-red-700">{profilingError.message}</p>
              {profilingError.retryable && (
                <p className="mt-2 text-sm text-red-600">
                  Please go back and try uploading your file again.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if no profile
  if (!dataProfile) {
    return (
      <div className="space-y-6">
        <div className="p-8 text-center">
          <p className="text-gray-600">No data profile available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 flex-1 min-h-0">
      <div className="shrink-0">
        <SourceFileLabel fileName={displayFileName} />
        <p className="mt-2 text-sm text-muted-foreground">
          Based on validated rows only. Rows with errors are excluded.
        </p>
      </div>

      {/* Summary Statistics */}
      <div className="shrink-0 grid grid-cols-4 gap-3">
        <StatsCard
          title={dataProfile.statistics.totalColumns}
          description="Total Columns"
          size="compact"
          className="border rounded-lg shadow-none"
        />
        <StatsCard
          title={dataProfile.statistics.categoricalColumns}
          description="Categorical Columns"
          size="compact"
          className="border rounded-lg shadow-none"
        />
        <StatsCard
          title={dataProfile.statistics.goodRows}
          description="Good Rows"
          size="compact"
          className="border rounded-lg shadow-none"
        />
        <StatsCard
          title={dataProfile.statistics.errorRows}
          description="Error Rows"
          size="compact"
          className="border rounded-lg shadow-none"
        />
      </div>

      {/* Column Summary Table */}
      <div className="flex-1 min-h-0 flex flex-col gap-2">
        <h4 className="shrink-0 text-sm font-semibold">Column Summary</h4>
        <div className="flex-1 min-h-0 rounded-lg border overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>
                  <div className="flex items-center gap-1.5">
                    Data Type
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-3 w-3 text-muted-foreground/60" />
                      </TooltipTrigger>
                      <TooltipContent side="top" sideOffset={4}>
                        Auto-detected. You can change these before adding your table.
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </TableHead>
                <TableHead>Categorical</TableHead>
                <TableHead>Null %</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dataProfile.columns.map((column: ColumnProfileDisplay, index: number) => (
                <TableRow key={index}>
                  <TableCell className="font-medium">{column.name}</TableCell>
                  <TableCell>{column.type}</TableCell>
                  <TableCell>{column.categorical ? "yes" : "no"}</TableCell>
                  <TableCell>
                    <NullPercentCell value={column.nullPercent} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};
